import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { AlertTriangle, FileText } from "lucide-react";

/**
 * CafFoliosBanner
 * Muestra los folios disponibles por tipo de DTE, respetando qué documentos
 * puede emitir el terminal actual (según flags puede_emitir_*).
 *
 * Se auto-refresca cada 60s. El aviso se dispara con el mínimo que configura
 * cada empresa por tipo de documento (Configuración SII → Archivos CAF).
 * Nunca interrumpe la venta: es solo un banner.
 */

// Mapeo tipo_dte SII → flag del terminal
const TIPO_DTE_TO_FLAG = {
  "39": "puede_emitir_boleta_afecta",
  "41": "puede_emitir_boleta_exenta",
  "33": "puede_emitir_factura",
  "34": "puede_emitir_factura",  // factura exenta también cuenta como "puede_emitir_factura"
  "52": "puede_emitir_guia_despacho",
  "61": "puede_emitir_nota_credito",
};

const SEVERITY_STYLES = {
  OK: "bg-emerald-50 text-emerald-800 border-emerald-200",
  BAJO: "bg-amber-50 text-amber-800 border-amber-200",
  CRITICO: "bg-rose-100 text-rose-900 border-rose-300 animate-pulse",
};

export default function CafFoliosBanner({ terminal }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const r = await api.get("/sii/caf/summary", { silent402: true });
      setRows(r.data || []);
    } catch (_) {
      // silencioso: si SII no está configurado, ocultamos el banner
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 60000);
    return () => clearInterval(id);
  }, []);

  // Solo mostramos los tipos que el terminal tiene habilitados
  const visibleRows = useMemo(() => {
    if (!terminal) return [];
    // Fallback: si el terminal es antiguo y no tiene ningún flag `puede_emitir_*`
    // seteado, inferimos por su `ticket_mode` para mantener compatibilidad.
    const flags = ["puede_emitir_boleta_afecta", "puede_emitir_boleta_exenta", "puede_emitir_factura", "puede_emitir_nota_credito", "puede_emitir_guia_despacho"];
    const hasAnyFlag = flags.some((f) => terminal[f] === true || terminal[f] === false);
    const inferred = { ...terminal };
    if (!hasAnyFlag) {
      const modeMap = { BOLETA: "puede_emitir_boleta_afecta", FACTURA: "puede_emitir_factura", GUIA: "puede_emitir_guia_despacho", NC: "puede_emitir_nota_credito" };
      const flag = modeMap[terminal.ticket_mode];
      if (flag) inferred[flag] = true;
    }
    return rows.filter((r) => {
      const flag = TIPO_DTE_TO_FLAG[r.tipo_dte];
      return flag && inferred[flag] === true;
    });
  }, [rows, terminal]);

  if (loading || visibleRows.length === 0) return null;

  const hasCritico = visibleRows.some((r) => r.severidad === "CRITICO");
  const hasBajo = visibleRows.some((r) => r.severidad === "BAJO");
  const bajoMinimo = visibleRows.filter((r) => r.bajo_minimo);

  return (
    <div
      data-testid="pos-caf-banner"
      className={`pos-caf-banner px-4 py-2 border-b print:hidden ${hasCritico ? "bg-rose-50/60 border-rose-200" : hasBajo ? "bg-amber-50/50 border-amber-200" : "bg-emerald-50/40 border-emerald-100"}`}
    >
      <div className="max-w-7xl mx-auto flex items-center gap-3 flex-wrap">
        <span className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-slate-600 shrink-0">
          {hasCritico ? <AlertTriangle className="w-3.5 h-3.5 text-rose-600" /> : <FileText className="w-3.5 h-3.5 text-slate-500" />}
          Folios SII disponibles
        </span>
        <div className="flex flex-wrap gap-1.5">
          {visibleRows.map((r) => (
            <span
              key={r.tipo_dte}
              data-testid={`pos-caf-chip-${r.tipo_dte}`}
              className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full border ${SEVERITY_STYLES[r.severidad] || SEVERITY_STYLES.OK}`}
              title={`Tipo ${r.tipo_dte} · ${r.severidad}${r.minimo ? ` · avisa bajo ${r.minimo}` : ""}`}
            >
              {r.label}:
              <span className="font-mono-num font-bold">{r.folios_disponibles}</span>
            </span>
          ))}
        </div>
        {bajoMinimo.length > 0 && (
          <span data-testid="pos-caf-min-warning"
                className="pos-caf-warning w-full mt-1.5 flex items-center gap-2 text-xs sm:text-sm font-extrabold text-rose-700 bg-rose-100 border-2 border-rose-300 rounded-xl px-3 py-2 animate-pulse">
            <AlertTriangle className="w-4 h-4 shrink-0" />
            {bajoMinimo.map((r) => `Quedan ${r.folios_disponibles} folios de ${r.label} · mínimo configurado ${r.minimo}`).join(" · ")}
            <span className="pos-caf-warning-extra font-bold">· puedes seguir vendiendo</span>
          </span>
        )}
      </div>
    </div>
  );
}
